import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Badge,
  DataGrid,
  EmptyState,
  FilterBar,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-filing-lifecycle';
import { FileText } from 'lucide-react';
import { useFilings } from '../../hooks/useUccFilings';
import { formatDate, NOT_RECORDED } from './format';
import { ListHeader } from '../collateral/CollateralListParts';

type UccFiling = components['schemas']['UccFiling'];
type FilingStatusValue = components['schemas']['FilingStatus'];
type FilingTypeValue = components['schemas']['FilingType'];

/** The same page size as UCC Filings and Filings Awaiting Review. */
const PAGE_SIZE = 20;

/**
 * Label and badge variant per lifecycle FilingStatus. Keyed on the generated
 * union so a status added to ucc-filing-lifecycle.openapi.yaml fails tsc here
 * until it has a label, instead of printing its raw code.
 */
export const FILING_STATUS: Record<
  FilingStatusValue,
  { label: string; variant: 'success' | 'warning' | 'error' | 'neutral' | 'info' }
> = {
  draft: { label: 'Draft', variant: 'neutral' },
  submitted: { label: 'Submitted', variant: 'warning' },
  rejected: { label: 'Rejected', variant: 'error' },
  acknowledged: { label: 'Acknowledged', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  amended: { label: 'Amended', variant: 'info' },
  continued: { label: 'Continued', variant: 'info' },
  terminated: { label: 'Terminated', variant: 'neutral' },
  lapsed: { label: 'Lapsed', variant: 'error' },
  imported: { label: 'Imported', variant: 'neutral' },
};

/** Labels per lifecycle FilingType (ucc1 / ucc3Amendment, the lifecycle spelling). */
export const FILING_TYPE_LABELS: Record<FilingTypeValue, string> = {
  ucc1: 'UCC-1',
  ucc3Amendment: 'UCC-3 Amendment',
  ucc3Continuation: 'UCC-3 Continuation',
  ucc3Termination: 'UCC-3 Termination',
};

/**
 * The open statuses this page covers: filings not yet accepted by a filing
 * office. The lifecycle contract has no pending-submission status; a filing
 * queued for submission is `draft` until it is `submitted`.
 */
export const OPEN_STATUSES: readonly FilingStatusValue[] = ['draft', 'submitted', 'rejected'];

const OPEN_STATUS_SET: ReadonlySet<string> = new Set(OPEN_STATUSES);
const FILING_TYPES = Object.keys(FILING_TYPE_LABELS) as FilingTypeValue[];

/**
 * The kit FilterBar's definitions, as on the other list pages: a filing-number
 * search plus a Status and a Filing type chip. No Status chip means every open
 * status. The page rendered its own labeled Input and two Selects (UI run r3,
 * fi-cons-12).
 */
const FILTERS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: OPEN_STATUSES.map((value) => ({ value, label: FILING_STATUS[value].label })),
  },
  {
    id: 'filingType',
    label: 'Filing type',
    type: 'select',
    options: FILING_TYPES.map((value) => ({ value, label: FILING_TYPE_LABELS[value] })),
  },
];

function isOpenStatus(value: string | undefined): value is FilingStatusValue {
  return value != null && OPEN_STATUS_SET.has(value);
}

function isFilingType(value: string | undefined): value is FilingTypeValue {
  return value != null && Object.prototype.hasOwnProperty.call(FILING_TYPE_LABELS, value);
}

/**
 * One server count per open status, independent of the table filter, so a
 * card reads the institution's whole queue rather than the page on screen.
 * `limit: 1` keeps each read to its `count`.
 */
function useOpenStatusCount(tenantId: string, status: FilingStatusValue) {
  const query = useFilings({ tenantId, status, page: 1, limit: 1 });
  return {
    count: query.data?.count,
    isError: query.isError,
  };
}

function cardValue(count: number | undefined): string {
  return count == null ? NOT_RECORDED : count.toLocaleString('en-US');
}

function cardCaption(count: number | undefined, isError: boolean): string | undefined {
  if (count != null) return undefined;
  return isError ? METRIC_READ_CAPTIONS.failed : METRIC_READ_CAPTIONS.unread;
}

export function FilingStatus() {
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const statusValue = activeFilters.find((f) => f.filterId === 'status')?.value;
  const typeValue = activeFilters.find((f) => f.filterId === 'filingType')?.value;
  const statusFilter: FilingStatusValue | '' = isOpenStatus(statusValue) ? statusValue : '';
  const typeFilter: FilingTypeValue | '' = isFilingType(typeValue) ? typeValue : '';

  const { data, isLoading, isError, error, refetch, isFetching } = useFilings({
    tenantId,
    status: statusFilter || undefined,
    filingType: typeFilter || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const draft = useOpenStatusCount(tenantId, 'draft');
  const submitted = useOpenStatusCount(tenantId, 'submitted');
  const rejected = useOpenStatusCount(tenantId, 'rejected');
  const openTotal =
    draft.count != null && submitted.count != null && rejected.count != null
      ? draft.count + submitted.count + rejected.count
      : undefined;

  const pageRows: UccFiling[] = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  // listFilings takes one status. With no status chosen, the server page holds
  // every status and this page keeps the open ones.
  const filings = statusFilter ? pageRows : pageRows.filter((f) => OPEN_STATUS_SET.has(f.status));

  const filtered = searchQuery
    ? filings.filter((f) => (f.filingNumber ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    : filings;

  const userFiltered = !!statusFilter || !!typeFilter || !!searchQuery;

  const columns: ColumnDef<UccFiling, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'filingNumber',
        header: 'Filing number',
        cell: ({ row }) => (
          <span className="text-sm font-mono font-medium text-secondary">
            {row.original.filingNumber || 'Not yet assigned'}
          </span>
        ),
      },
      {
        accessorKey: 'filingType',
        header: 'Type',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary">
            {FILING_TYPE_LABELS[row.original.filingType] ?? row.original.filingType}
          </span>
        ),
      },
      {
        accessorKey: 'jurisdictionCode',
        header: 'Jurisdiction',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">
            {row.original.jurisdictionCode || NOT_RECORDED}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const entry = FILING_STATUS[row.original.status];
          return entry ? (
            <Badge variant={entry.variant} size="sm">
              {entry.label}
            </Badge>
          ) : (
            <span className="text-sm text-tertiary">{row.original.status}</span>
          );
        },
      },
      {
        accessorKey: 'filingDate',
        header: 'Filed',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary tabular-nums">
            {row.original.filingDate ? formatDate(row.original.filingDate) : 'Not filed'}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last updated',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary tabular-nums">
            {formatDate(row.original.updatedAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      {/* The shared list header and tile row, so this page reflows like the
          rest of the Lien Filings group at phone width (UI run r3, fi-g5-4). */}
      <ListHeader
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Open UCC filings: drafts, filings submitted and awaiting the filing
            office, and rejected filings.
          </p>
        }
      />

      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Open filings"
          value={cardValue(openTotal)}
          caption={cardCaption(openTotal, draft.isError || submitted.isError || rejected.isError)}
        />
        <MetricCard label="Draft" value={cardValue(draft.count)} caption={cardCaption(draft.count, draft.isError)} />
        <MetricCard
          label="Submitted"
          value={cardValue(submitted.count)}
          caption={cardCaption(submitted.count, submitted.isError)}
        />
        <MetricCard
          label="Rejected"
          value={cardValue(rejected.count)}
          caption={cardCaption(rejected.count, rejected.isError)}
        />
      </MetricCardGrid>

      <FilterBar
        filters={FILTERS}
        activeFilters={activeFilters}
        onFiltersChange={(next) => {
          setActiveFilters(next);
          setPage(1);
        }}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by filing number"
        className="rounded-t-sm"
      />

      <DataGrid<UccFiling>
        data={filtered}
        columns={columns}
        isLoading={isLoading}
        errorMessage={isError ? `Filings could not be read: ${(error as Error).message}` : undefined}
        emptyState={
          <EmptyState
            icon={<FileText className="h-8 w-8" />}
            title={
              userFiltered ? 'No filings match these filters' : 'No open UCC filings for your institution'
            }
            description={
              userFiltered
                ? 'Clear the search or remove a filter.'
                : 'Drafts, submitted filings and rejected filings appear here until a filing office accepts them.'
            }
          />
        }
        pagination={{
          page: page - 1,
          pageSize: PAGE_SIZE,
          total: totalCount,
          onPageChange: (next) => setPage(next + 1),
        }}
        getRowId={(row) => row.filingId}
        hoverable
        className="rounded-b-sm shadow-sm"
      />
    </div>
  );
}
